// ============================================
// P0 阴影修复（2026-10-15）：平行光（太阳）阴影参数与范围自适应
// 背景：原 SunLight 阴影相机写死 ±30m/far 100 → 大于 60m 的场景没有影子。
// 方案：阴影相机范围按场景半径自适应（auto），或手动指定；far 随范围放大；
//       太阳（平行光）位置距离也随场景半径外移，保证大场景阴影完整且方向一致。
// ============================================

export interface ShadowSettings {
  /** 阴影相机范围自适应场景半径（默认 true） */
  auto: boolean;
  /** 手动范围（米；auto=false 时生效） */
  radius: number;
  /** 阴影贴图分辨率（1024 | 2048 | 4096） */
  mapSize: number;
  /** 阴影强度 0..1（0=关闭阴影） */
  intensity: number;
  bias: number;
  normalBias: number;
}

export const DEFAULT_SHADOW_SETTINGS: ShadowSettings = {
  auto: true,
  radius: 60,
  mapSize: 2048,
  intensity: 1,
  bias: -0.001,
  normalBias: 0.02,
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** 归一化（旧场景无 shadow 字段/非法值 → 默认；分辨率吸附到 1024/2048/4096） */
export function normalizeShadowSettings(raw?: Partial<ShadowSettings> | null): ShadowSettings {
  const r = (raw || {}) as Partial<ShadowSettings>;
  const mapSizeRaw = Number(r.mapSize);
  const mapSize = [1024, 2048, 4096].includes(mapSizeRaw) ? mapSizeRaw : DEFAULT_SHADOW_SETTINGS.mapSize;
  return {
    auto: r.auto !== undefined ? r.auto === true : DEFAULT_SHADOW_SETTINGS.auto,
    radius: Number.isFinite(Number(r.radius)) && Number(r.radius) > 0 ? Number(r.radius) : DEFAULT_SHADOW_SETTINGS.radius,
    mapSize,
    intensity: Number.isFinite(Number(r.intensity)) ? clamp(Number(r.intensity), 0, 1) : DEFAULT_SHADOW_SETTINGS.intensity,
    bias: Number.isFinite(Number(r.bias)) ? Number(r.bias) : DEFAULT_SHADOW_SETTINGS.bias,
    normalBias: Number.isFinite(Number(r.normalBias)) ? Number(r.normalBias) : DEFAULT_SHADOW_SETTINGS.normalBias,
  };
}

/** 阴影相机范围：auto → max(30, 场景半径 × 1.25)；manual → radius；far = extent × 2.5 */
export function shadowExtent(sceneRadius: number, settings: ShadowSettings): { extent: number; far: number } {
  const r = Number.isFinite(sceneRadius) && sceneRadius > 0 ? sceneRadius : 100;
  const base = settings.auto ? Math.max(30, r * 1.25) : Math.max(10, settings.radius);
  const extent = clamp(base, 10, 3000);
  return { extent, far: Math.max(50, extent * 2.5) };
}

/** 太阳（平行光）位置距离：随场景半径外移（≥50），保证大场景阴影方向稳定 */
export function sunLightDistance(sceneRadius: number): number {
  const r = Number.isFinite(sceneRadius) && sceneRadius > 0 ? sceneRadius : 100;
  return Math.max(50, r * 1.5);
}
